import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap'
import CatEmpleados from '../../../Services/Catalogos/CatEmpleados'
import { MsgInformativo } from '../../../Utils/Toast/msgInformativo'
import './InfoGeneralEmpleado.css'
import {
  DTOEmpleadoInfoGeneral,
  DTOEmpleadoInfoGeneralDetalle,
  DTOEmpleadoInfoGeneralValores,
  DTOGuardarEmpleadoInfoGeneral,
  DTOActualizarEmpleadoInfoGeneral,
} from './DTO/DTOEmpleadoInfoGeneral'

interface InfoGeneralEmpleadoProps {
  show: boolean
  numEmpleado: string
  onClose: () => void
}

interface FormularioInfoGeneral {
  fechaVigenciaDesde: string
  fechaEvaluacion: string
  promocion: boolean
  puestoResultado: string
  salarioDiario: string
  sdi: string
  semanalBruto: string
  mensualBruto: string
  semanalNeto: string
  mensualNeto: string
  tieneAutomovil: boolean
  gasolinaMensual: string
}



const formularioInicial = (): FormularioInfoGeneral => ({
  fechaVigenciaDesde: '',
  fechaEvaluacion: '',
  promocion: false,
  puestoResultado: '',
  salarioDiario: '',
  sdi: '',
  semanalBruto: '',
  mensualBruto: '',
  semanalNeto: '',
  mensualNeto: '',
  tieneAutomovil: false,
  gasolinaMensual: '',
})

const valorFormulario = (valor: number | null) =>
  valor === null || valor === undefined ? '' : String(valor)

const numeroONull = (valor: string): number | null => {
  if (valor.trim() === '') return null
  const numero = Number(valor)
  return Number.isFinite(numero) ? numero : null
}

const valoresFormulario = (formulario: FormularioInfoGeneral): DTOEmpleadoInfoGeneralValores => ({
  fechaEvaluacion: formulario.fechaEvaluacion || null,
  promocion: formulario.promocion,
  puestoResultado: formulario.puestoResultado.trim() || null,
  salarioDiario: numeroONull(formulario.salarioDiario),
  sdi: numeroONull(formulario.sdi),
  semanalBruto: numeroONull(formulario.semanalBruto),
  mensualBruto: numeroONull(formulario.mensualBruto),
  semanalNeto: numeroONull(formulario.semanalNeto),
  mensualNeto: numeroONull(formulario.mensualNeto),
  tieneAutomovil: formulario.tieneAutomovil,
  gasolinaMensual: numeroONull(formulario.gasolinaMensual),
})

const formatoMoneda = (valor: number | null) =>
  valor === null || valor === undefined
    ? 'No Aplica'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(valor)

const formatoFecha = (valor: string | null) => {
  if (!valor) return 'Abierta'
  const partes = valor.slice(0, 10).split('-')
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : valor
}

const calcularAniosServicio = (fechaIngreso: string | null) => {
  if (!fechaIngreso) return null

  const partes = fechaIngreso.slice(0, 10).split('-').map(Number)
  if (partes.length !== 3 || partes.some((parte) => !Number.isFinite(parte))) {
    return null
  }

  const [anio, mes, dia] = partes
  const hoy = new Date()
  let anios = hoy.getFullYear() - anio

  if (
    hoy.getMonth() + 1 < mes ||
    (hoy.getMonth() + 1 === mes && hoy.getDate() < dia)
  ) {
    anios -= 1
  }

  return Math.max(0, anios)
}

export default function InfoGeneralEmpleado({
  show,
  numEmpleado,
  onClose,
}: InfoGeneralEmpleadoProps) {
  const [detalle, setDetalle] = useState<DTOEmpleadoInfoGeneralDetalle | null>(null)
  const [formulario, setFormulario] = useState<FormularioInfoGeneral>(formularioInicial())
  const [registroCorreccion, setRegistroCorreccion] = useState<DTOEmpleadoInfoGeneral | null>(null)
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [guardandoImagenes, setGuardandoImagenes] = useState(false)
  const [fotoArchivo, setFotoArchivo] = useState<File | null>(null)
  const [firmaArchivo, setFirmaArchivo] = useState<File | null>(null)
  const [fotoUrl, setFotoUrl] = useState<string | null>(null)
  const [firmaUrl, setFirmaUrl] = useState<string | null>(null)
  const fotoUrlRef = useRef<string | null>(null)
  const firmaUrlRef = useRef<string | null>(null)
  const [showMsg, setShowMsg] = useState(false)
  const [msg, setMsg] = useState('')
  const [header, setHeader] = useState('')
  const [msgColor, setMsgColor] = useState('primary')

  const mostrarMensaje = (
    encabezado: string,
    mensaje: string,
    color: string = 'primary'
  ) => {
    setHeader(encabezado)
    setMsg(mensaje)
    setMsgColor(color)
    setShowMsg(true)
  }

  const historialVisible = [...(detalle?.historial ?? [])]
    .filter((registro) => !registro.esAnulado)
    .sort((a, b) => {
      const aEsVigente = !a.fechaVigenciaHasta
      const bEsVigente = !b.fechaVigenciaHasta

      if (aEsVigente !== bEsVigente) {
        return aEsVigente ? 1 : -1
      }

      const comparacionFecha = a.fechaVigenciaDesde
        .slice(0, 10)
        .localeCompare(b.fechaVigenciaDesde.slice(0, 10))

      return comparacionFecha !== 0
        ? comparacionFecha
        : a.idInfoGeneral - b.idInfoGeneral
    })

  const registroVigente = historialVisible.find(
    (registro) => !registro.fechaVigenciaHasta
  ) ?? null

  const cargarInfoGeneral = async () => {
    if (!numEmpleado) return

    setCargando(true)
    setShowMsg(false)

    try {
      const response = await CatEmpleados.GetInfoGeneral(numEmpleado)
      setDetalle(response.data)
      setFormulario((actual) => ({
        ...actual,
        puestoResultado: actual.promocion
          ? actual.puestoResultado
          : response.data.puesto || '',
      }))
    } catch (e: any) {
      mostrarMensaje(
        'Error',
        e?.response?.data?.message || e?.message || 'No fue posible consultar la información general.',
        'danger'
      )
    } finally {
      setCargando(false)
    }
  }

  const cargarFotosFirmas = async () => {
    if (!numEmpleado) return

    const [foto, firma] = await Promise.allSettled([
      CatEmpleados.GetFotoFirma(numEmpleado, 'foto'),
      CatEmpleados.GetFotoFirma(numEmpleado, 'firma'),
    ])

    const nuevaFotoUrl = foto.status === 'fulfilled' ? foto.value : null
    const nuevaFirmaUrl = firma.status === 'fulfilled' ? firma.value : null

    if (fotoUrlRef.current) URL.revokeObjectURL(fotoUrlRef.current)
    if (firmaUrlRef.current) URL.revokeObjectURL(firmaUrlRef.current)

    fotoUrlRef.current = nuevaFotoUrl
    firmaUrlRef.current = nuevaFirmaUrl
    setFotoUrl(nuevaFotoUrl)
    setFirmaUrl(nuevaFirmaUrl)
  }

  useEffect(() => {
    return () => {
      if (fotoUrlRef.current) URL.revokeObjectURL(fotoUrlRef.current)
      if (firmaUrlRef.current) URL.revokeObjectURL(firmaUrlRef.current)
    }
  }, [])

  useEffect(() => {
    if (show && numEmpleado) {
      setDetalle(null)
      setFormulario(formularioInicial())
      setRegistroCorreccion(null)
      setFotoArchivo(null)
      setFirmaArchivo(null)
      setShowMsg(false)
      cargarInfoGeneral()
      cargarFotosFirmas()
    } else if (!show) {
      if (fotoUrlRef.current) URL.revokeObjectURL(fotoUrlRef.current)
      if (firmaUrlRef.current) URL.revokeObjectURL(firmaUrlRef.current)
      fotoUrlRef.current = null
      firmaUrlRef.current = null
      setFotoUrl(null)
      setFirmaUrl(null)
    }
    // cargarInfoGeneral depende únicamente del empleado recibido al abrir el modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, numEmpleado])

  const seleccionarImagen = (
    tipo: 'foto' | 'firma',
    archivo: File | null
  ) => {
    if (!archivo) {
      tipo === 'foto' ? setFotoArchivo(null) : setFirmaArchivo(null)
      return
    }

    const extension = archivo.name.split('.').pop()?.toLowerCase()
    if (!extension || !['jpg', 'jpeg', 'png'].includes(extension)) {
      mostrarMensaje('Error', 'La imagen debe ser JPG, JPEG o PNG.', 'danger')
      return
    }

    if (archivo.size > 5 * 1024 * 1024) {
      mostrarMensaje('Error', 'La imagen no debe exceder los 5 MB.', 'danger')
      return
    }

    setShowMsg(false)
    tipo === 'foto' ? setFotoArchivo(archivo) : setFirmaArchivo(archivo)
  }

  const guardarFotosFirmas = async () => {
    if (!fotoArchivo && !firmaArchivo) {
      mostrarMensaje('Error', 'Selecciona una foto o una firma para guardar.', 'danger')
      return
    }

    setGuardandoImagenes(true)
    setShowMsg(false)

    try {
      await CatEmpleados.SaveFotoFirma(numEmpleado, fotoArchivo, firmaArchivo)
      setFotoArchivo(null)
      setFirmaArchivo(null)
      await cargarFotosFirmas()
      mostrarMensaje('Actualizado', 'La foto y firma del empleado se actualizaron correctamente.', 'success')
    } catch (e: any) {
      mostrarMensaje(
        'Error',
        e?.response?.data?.message || e?.message || 'No fue posible guardar la foto o firma.',
        'danger'
      )
    } finally {
      setGuardandoImagenes(false)
    }
  }

  const actualizarCampo = (campo: keyof FormularioInfoGeneral, valor: string | boolean) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }))
  }

  const calcularAutomatico = (valor: string, operacion: (numero: number) => number) => {
    if (valor.trim() === '') return ''

    const numero = Number(valor)
    return Number.isFinite(numero) ? operacion(numero).toFixed(2) : ''
  }

  const actualizarSalarioDiario = (valor: string) => {
    setFormulario((actual) => ({
      ...actual,
      salarioDiario: valor,
      semanalBruto: calcularAutomatico(valor, (numero) => numero * 7),
      mensualBruto: calcularAutomatico(valor, (numero) => numero * 30.4),
    }))
  }

  const actualizarSemanalNeto = (valor: string) => {
    setFormulario((actual) => ({
      ...actual,
      semanalNeto: valor,
      mensualNeto: calcularAutomatico(valor, (numero) => (numero / 7) * 30.4),
    }))
  }

  const actualizarPromocion = (promocion: boolean) => {
    const puestoSinPromocion = registroCorreccion?.puestoResultado
      || detalle?.puesto
      || ''

    setFormulario((actual) => ({
      ...actual,
      promocion,
      puestoResultado: promocion ? '' : puestoSinPromocion,
    }))
  }

  const iniciarCorreccion = (registro: DTOEmpleadoInfoGeneral) => {
    setRegistroCorreccion(registro)
    setFormulario({
      fechaVigenciaDesde: registro.fechaVigenciaDesde.slice(0, 10),
      fechaEvaluacion: registro.fechaEvaluacion?.slice(0, 10) || '',
      promocion: registro.promocion,
      puestoResultado: registro.puestoResultado || detalle?.puesto || '',
      salarioDiario: valorFormulario(registro.salarioDiario),
      sdi: valorFormulario(registro.sdi),
      semanalBruto: valorFormulario(registro.semanalBruto),
      mensualBruto: valorFormulario(registro.mensualBruto),
      semanalNeto: valorFormulario(registro.semanalNeto),
      mensualNeto: valorFormulario(registro.mensualNeto),
      tieneAutomovil: registro.tieneAutomovil,
      gasolinaMensual: valorFormulario(registro.gasolinaMensual),
    })
    setShowMsg(false)
  }

  const cancelarCorreccion = () => {
    setRegistroCorreccion(null)
    setFormulario({
      ...formularioInicial(),
      puestoResultado: detalle?.puesto || '',
    })
    setShowMsg(false)
  }

  const guardar = async () => {
    if (!detalle) return

    if (!registroCorreccion && !formulario.fechaVigenciaDesde) {
      mostrarMensaje('Error', 'La fecha de vigencia es obligatoria.', 'danger')
      return
    }


    if (!formulario.fechaEvaluacion) {
      mostrarMensaje('Error', 'La fecha de evaluación es obligatoria.', 'danger')
      return
    }

    if (formulario.promocion && !formulario.puestoResultado.trim()) {
      mostrarMensaje('Error', 'El nuevo puesto es obligatorio cuando existe promoción.', 'danger')
      return
    }

    setGuardando(true)
    setShowMsg(false)

    try {
      if (registroCorreccion) {
        const request: DTOActualizarEmpleadoInfoGeneral = {
          idInfoGeneral: registroCorreccion.idInfoGeneral,
          ...valoresFormulario(formulario),
        }
        await CatEmpleados.ActualizarInfoGeneral(request)
        mostrarMensaje('Actualizado', 'El registro fue actualizado correctamente.', 'success')
      } else {
        const request: DTOGuardarEmpleadoInfoGeneral = {
          numEmpleado: detalle.numEmpleado,
          fechaVigenciaDesde: formulario.fechaVigenciaDesde,
          ...valoresFormulario(formulario),
        }
        await CatEmpleados.CrearVigenciaInfoGeneral(request)
        mostrarMensaje('Guardado', 'La nueva vigencia fue guardada correctamente.', 'success')
      }

      setRegistroCorreccion(null)
      setFormulario(formularioInicial())
      await cargarInfoGeneral()
    } catch (e: any) {
      mostrarMensaje(
        'Error',
        e?.response?.data?.message || e?.message || 'No fue posible guardar la información general.',
        'danger'
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="info-general-modal"
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>Info General del Empleado</Modal.Title>
      </Modal.Header>

      <MsgInformativo
        show={showMsg}
        msg={msg}
        header={header}
        msgColor={msgColor}
        closeToast={() => setShowMsg(false)}
      />

      <Modal.Body>
        {cargando && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2">Consultando información...</div>
          </div>
        )}

        {!cargando && detalle && (
          <>
            <div className="info-general-imagenes border rounded p-3 mb-3">
              <ArchivoEmpleado
                titulo="Foto del empleado"
                tipo="foto"
                imagenUrl={fotoUrl}
                archivo={fotoArchivo}
                onChange={(archivo) => seleccionarImagen('foto', archivo)}
              />
              <ArchivoEmpleado
                titulo="Firma del empleado"
                tipo="firma"
                imagenUrl={firmaUrl}
                archivo={firmaArchivo}
                onChange={(archivo) => seleccionarImagen('firma', archivo)}
              />
              <div className="info-general-imagenes-acciones">
                <small className="text-muted">Formatos JPG o PNG, máximo 5 MB.</small>
                <Button
                  variant="outline-primary"
                  onClick={guardarFotosFirmas}
                  disabled={guardandoImagenes || (!fotoArchivo && !firmaArchivo)}
                >
                  {guardandoImagenes ? 'Guardando...' : 'Guardar foto y firma'}
                </Button>
              </div>
            </div>

            <div className="border rounded p-3 mb-3 bg-light">
              <div className="info-general-resumen">
                <div><strong>No. empleado</strong><div>{detalle.numEmpleado}</div></div>
                <div><strong>Nombre</strong><div>{detalle.name || 'Sin capturar'}</div></div>
                <div><strong>Oficina</strong><div>{detalle.oficina || 'Sin capturar'}</div></div>
                <div><strong>Puesto</strong><div>{detalle.puesto || 'Sin capturar'}</div></div>
                <div><strong>Área</strong><div>{detalle.departamento || 'Sin capturar'}</div></div>
                <div><strong>Fecha de ingreso</strong><div>{detalle.fechaIngreso ? formatoFecha(detalle.fechaIngreso) : 'Sin capturar'}</div></div>
                <div><strong>Tabulador</strong><div>{detalle.puesto || 'Sin capturar'}</div></div>
                <div>
                  <strong>Años de servicio</strong>
                  <div>
                    {calcularAniosServicio(detalle.fechaIngreso) === null
                      ? 'Sin capturar'
                      : `${calcularAniosServicio(detalle.fechaIngreso)} años`}
                  </div>
                </div>
              </div>
            </div>

            {registroVigente && (
              <Alert variant="info">
                Vigencia abierta desde <strong>{formatoFecha(registroVigente.fechaVigenciaDesde)}</strong> ·
                Mensual bruto <strong>{formatoMoneda(registroVigente.mensualBruto)}</strong> ·
                Mensual neto <strong>{formatoMoneda(registroVigente.mensualNeto)}</strong>
              </Alert>
            )}

            <h6>{registroCorreccion ? 'Corregir registro' : 'Registrar nueva vigencia'}</h6>
            <Form className="border rounded p-3 mb-4">
              <div className="info-general-evaluacion">
              
                <Form.Group>
                  <Form.Label>Fecha de evaluación</Form.Label>
                  <Form.Control
                    type="date"
                    value={formulario.fechaEvaluacion}
                    onChange={(e) => actualizarCampo('fechaEvaluacion', e.target.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Fecha de ajuste</Form.Label>
                  <Form.Control
                    type="date"
                    value={formulario.fechaVigenciaDesde}
                    disabled={registroCorreccion !== null}
                    onChange={(e) => actualizarCampo('fechaVigenciaDesde', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Captura la fecha real en que comenzaron a aplicar estos importes.
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Promoción</Form.Label>
                  <Form.Select
                    value={formulario.promocion ? 'si' : 'no'}
                    onChange={(e) => actualizarPromocion(e.target.value === 'si')}
                  >
                    <option value="no">No</option>
                    <option value="si">Sí</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label>{formulario.promocion ? 'Nuevo puesto' : 'Puesto resultado'}</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={formulario.puestoResultado}
                    readOnly={!formulario.promocion}
                    className={!formulario.promocion ? 'bg-light' : undefined}
                    placeholder={formulario.promocion ? 'Captura el nuevo puesto' : 'Puesto actual del empleado'}
                    onChange={(e) => actualizarCampo('puestoResultado', e.target.value)}
                  />
                </Form.Group>
              </div>

              <div className="info-general-campos">
                <CampoImporte label="Salario diario" value={formulario.salarioDiario} decimals={4} onChange={actualizarSalarioDiario} />
                <CampoImporte label="SDI" value={formulario.sdi} decimals={4} onChange={(v) => actualizarCampo('sdi', v)} />
                <CampoImporte label="Semanal bruto" value={formulario.semanalBruto} readOnly />
                <CampoImporte label="Mensual bruto" value={formulario.mensualBruto} readOnly />
                <CampoImporte label="Semanal neto" value={formulario.semanalNeto} onChange={actualizarSemanalNeto} />
                <CampoImporte label="Mensual neto" value={formulario.mensualNeto} readOnly />
                <div className="info-general-automovil">
                  <Form.Check
                    type="switch"
                    label="Tiene automóvil"
                    checked={formulario.tieneAutomovil}
                    onChange={(e) => actualizarCampo('tieneAutomovil', e.target.checked)}
                  />
                </div>
                <CampoImporte label="Gasolina mensual" value={formulario.gasolinaMensual} onChange={(v) => actualizarCampo('gasolinaMensual', v)} />
              </div>
              <div className="d-flex justify-content-end gap-2">
                {registroCorreccion && (
                  <Button variant="secondary" onClick={cancelarCorreccion} disabled={guardando}>
                    Cancelar corrección
                  </Button>
                )}
                <Button variant={registroCorreccion ? 'warning' : 'primary'} onClick={guardar} disabled={guardando}>
                  {guardando ? 'Guardando...' : registroCorreccion ? 'Guardar corrección' : 'Guardar vigencia'}
                </Button>
              </div>
            </Form>

            <h6>Historial</h6>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Vigencia</th>
                    <th>Fecha evaluación</th>
                    <th>Promoción</th>
                    <th>Puesto resultado</th>
                    <th>Salario diario</th>
                    <th>SDI</th>
                    <th>Semanal bruto</th>
                    <th>Mensual bruto</th>
                    <th>Semanal neto</th>
                    <th>Mensual neto</th>
                    <th>Automóvil</th>
                    <th>Gasolina</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {historialVisible.length === 0 && (
                    <tr><td colSpan={14} className="text-center text-muted">Sin información registrada.</td></tr>
                  )}
                  {historialVisible.map((registro) => (
                    <tr key={registro.idInfoGeneral}>
                      <td>{formatoFecha(registro.fechaVigenciaDesde)} - {formatoFecha(registro.fechaVigenciaHasta)}</td>
                      <td>{registro.fechaEvaluacion ? formatoFecha(registro.fechaEvaluacion) : 'Sin capturar'}</td>
                      <td>
                        <Badge bg={registro.promocion ? 'success' : 'secondary'}>
                          {registro.promocion ? 'Sí' : 'No'}
                        </Badge>
                      </td>
                      <td>{registro.puestoResultado || 'Sin capturar'}</td>
                      <td>{formatoMoneda(registro.salarioDiario)}</td>  
                      <td>{formatoMoneda(registro.sdi)}</td>
                      <td>{formatoMoneda(registro.semanalBruto)}</td>
                      <td>{formatoMoneda(registro.mensualBruto)}</td>
                      <td>{formatoMoneda(registro.semanalNeto)}</td>
                      <td>{formatoMoneda(registro.mensualNeto)}</td>
                      <td>{registro.tieneAutomovil ? 'Sí' : 'No'}</td>
                      <td>{formatoMoneda(registro.gasolinaMensual)}</td>
                      <td>
                        {!registro.fechaVigenciaHasta
                          ? <Badge bg="success">Vigente</Badge>
                          : <Badge bg="light" text="dark">Histórico</Badge>}
                      </td>
                      <td>
                        <Button size="sm" variant="outline-warning" onClick={() => iniciarCorreccion(registro)}>
                          Corregir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={guardando || guardandoImagenes}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  )
}

interface ArchivoEmpleadoProps {
  titulo: string
  tipo: 'foto' | 'firma'
  imagenUrl: string | null
  archivo: File | null
  onChange: (archivo: File | null) => void
}

function ArchivoEmpleado({ titulo, tipo, imagenUrl, archivo, onChange }: ArchivoEmpleadoProps) {
  return (
    <div className={`info-general-imagen info-general-imagen-${tipo}`}>
      <strong>{titulo}</strong>
      <div className="info-general-imagen-preview">
        {imagenUrl
          ? <img src={imagenUrl} alt={titulo} />
          : <span>Sin {tipo} registrada</span>}
      </div>
      <Form.Control
        key={`${tipo}-${archivo?.name ?? 'vacio'}`}
        type="file"
        size="sm"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        onChange={(e) => onChange((e.target as HTMLInputElement).files?.[0] || null)}
      />
      {archivo && <small className="text-muted text-truncate">Seleccionado: {archivo.name}</small>}
    </div>
  )
}

interface CampoImporteProps {
  label: string
  value: string
  decimals?: number
  onChange?: (value: string) => void
  readOnly?: boolean
}

function CampoImporte({ label, value, decimals = 2, onChange, readOnly = false }: CampoImporteProps) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        min="0"
        step={decimals === 4 ? '0.0001' : '0.01'}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly}
        className={readOnly ? 'bg-light' : undefined}
        placeholder={readOnly ? 'Automático' : 'Sin capturar'}
        title={readOnly ? 'Este importe se calcula automáticamente.' : undefined}
      />
    </Form.Group>
  )
}
